'use client';

/**
 * Mobile drawer wrapper — slides the expanded sidebar in from the left
 * with an overlay on viewports below `lg` (1024px). Retires
 * ADR-0036 §M1 once shipped.
 *
 * - Overlay click, ESC, or route change closes the drawer.
 * - Focus is moved into the drawer on open; ESC returns focus to the
 *   trigger via the close-button's ref handed back by the parent.
 * - The drawer body re-uses `<NavV2Sidebar />` so there is no second IA
 *   implementation — one component, two presentations.
 * - Body scroll is locked while the drawer is open.
 */

import { buttonVariants } from '@statura/ui';
import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { X } from 'lucide-react';
import { NavV2Sidebar, type NavV2SidebarProps } from './sidebar';

interface Props extends Omit<NavV2SidebarProps, 'onCollapse' | 'inMobileDrawer'> {
  open: boolean;
  onClose: () => void;
}

export function NavV2MobileDrawer({ open, onClose, ...sidebarProps }: Props) {
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const priorFocusRef = useRef<HTMLElement | null>(null);
  const pathname = usePathname();
  const initialPathnameRef = useRef(pathname);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'Tab') {
        const candidates = panelRef.current?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        const focusable = Array.from(candidates ?? []).filter((element) => {
          const style = window.getComputedStyle(element);
          return (
            !element.closest('[hidden], [aria-hidden="true"]') &&
            style.display !== 'none' &&
            style.visibility !== 'hidden'
          );
        });
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!first || !last) {
          e.preventDefault();
          panelRef.current?.focus();
          return;
        }
        if (
          e.shiftKey &&
          (document.activeElement === first || !panelRef.current?.contains(document.activeElement))
        ) {
          e.preventDefault();
          last.focus();
        } else if (
          !e.shiftKey &&
          (document.activeElement === last || !panelRef.current?.contains(document.activeElement))
        ) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Close on route change (Link click inside drawer)
  useEffect(() => {
    if (!open) {
      initialPathnameRef.current = pathname;
      return;
    }
    if (pathname !== initialPathnameRef.current) {
      onClose();
      initialPathnameRef.current = pathname;
    }
  }, [pathname, open, onClose]);

  // Body-scroll lock while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Focus the close button on open
  useEffect(() => {
    if (open) {
      priorFocusRef.current = document.activeElement as HTMLElement | null;
      // Defer to next tick so the element is mounted.
      const t = setTimeout(() => closeRef.current?.focus(), 0);
      return () => {
        clearTimeout(t);
        if (priorFocusRef.current?.isConnected) priorFocusRef.current.focus();
      };
    }
  }, [open]);

  if (!open) return null;

  return (
    <div
      data-print-chrome="true"
      className="fixed inset-0 z-[70] lg:hidden"
      role="dialog"
      aria-modal="true"
      aria-label="Workspace navigation"
    >
      {/* Overlay */}
      <button
        type="button"
        tabIndex={-1}
        aria-label="Close navigation menu"
        onClick={onClose}
        className="absolute inset-0 bg-surface-inverse/40 backdrop-blur-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus focus-visible:-outline-offset-2"
      />

      {/* Drawer panel */}
      <div
        ref={panelRef}
        tabIndex={-1}
        className="absolute inset-y-0 left-0 flex h-full w-72 max-w-full flex-col bg-surface-canvas shadow-dialog"
      >
        <div className="flex h-12 items-center justify-end px-2">
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close navigation menu"
            data-ui="button"
            className={buttonVariants({
              variant: 'ghost',
              size: 'default',
              className:
                '!p-0 w-11 focus:outline-none focus-visible:ring-2 focus-visible:ring-focus',
            })}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        <div className="flex-1 overflow-hidden">
          <NavV2Sidebar
            {...sidebarProps}
            // Phase 2 retro audit P2: the drawer renders at z-[70] but
            // the existing CommandPalette opens at z-50. Tapping the
            // sidebar Cmd-K trigger from inside the drawer rendered the
            // palette UNDER the drawer overlay — looked broken. Close
            // the drawer first, then dispatch the synthetic ⌘K (the
            // palette listens at the document level and will open as
            // soon as the keystroke fires).
            onCmdK={() => {
              onClose();
              sidebarProps.onCmdK();
            }}
            onHelpChat={
              sidebarProps.onHelpChat
                ? () => {
                    onClose();
                    sidebarProps.onHelpChat?.();
                  }
                : undefined
            }
            onCollapse={onClose}
            inMobileDrawer
          />
        </div>
      </div>
    </div>
  );
}
